import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import DashboardLayout from "@/components/Layout/DashboardLayout";
import Dashboard from "@/pages/Dashboard";
import Reservations from "@/pages/Reservations";
import ReservationDetail from "@/pages/ReservationDetail";
import FloorPlan from "@/pages/FloorPlan";
import Services from "@/pages/Services";
import Staff from "@/pages/Staff";
import Lounge from "@/pages/Lounge";
import Analytics from "@/pages/Analytics";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import PublicBooking from "@/pages/PublicBooking";

function ProtectedRoute({ children, roles }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-rooftop)]">
        <div className="text-[var(--text-muted)] text-sm" data-testid="auth-loading">SONARA · Lädt…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function RoleGuard({ allow, children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!allow.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

const MGMT_ROLES = ["owner", "manager", "admin"];

function Router() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/r/:slug" element={<PublicBooking />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute roles={["admin"]}>
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route
        element={
          <ProtectedRoute roles={["owner", "manager", "host", "waiter", "bartender", "admin"]}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/dashboard/reservations" element={<Reservations />} />
        <Route path="/dashboard/reservations/:id" element={<ReservationDetail />} />
        <Route path="/dashboard/floor-plan" element={<FloorPlan />} />
        <Route path="/dashboard/lounge" element={<Lounge />} />
        <Route path="/dashboard/services" element={<RoleGuard allow={MGMT_ROLES}><Services /></RoleGuard>} />
        <Route path="/dashboard/staff" element={<RoleGuard allow={MGMT_ROLES}><Staff /></RoleGuard>} />
        <Route path="/dashboard/analytics" element={<RoleGuard allow={MGMT_ROLES}><Analytics /></RoleGuard>} />
        <Route path="/dashboard/settings" element={<RoleGuard allow={MGMT_ROLES}><Settings /></RoleGuard>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <div className="App grain">
      <AuthProvider>
        <BrowserRouter>
          <Router />
        </BrowserRouter>
        <Toaster theme="dark" position="top-center" />
      </AuthProvider>
    </div>
  );
}
